import React, { useState, useContext, useEffect } from "react";
import { Card, FormLayout, PageActions, TextContainer, Banner } from "@shopify/polaris";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/react-hooks";
import fileDownload from "js-file-download";
import Baby from "babyparse";

// import hoc
import { withFeature } from "lib/hoc";

// import context
import { PrivateContext, OnboardingContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import component
import FileUpload from "lib/components/fileInput/fileUpload";
import { Sheet } from "lib/components";

// import configuration for downloading csv
import { addProductConfig } from "../../config";

// import query
import { BULK_UPLOAD } from "../../../../../../apollo/mutations";

const {
  gql: { BULK_PRODUCT_CSV: GQL_BULK_PRODUCT_CSV },
  BULK_PRODUCT_CSV,
  PRODUCT_BULK_CREATE,
} = constant;

const BulkCreate = (props) => {
  const { setBanner } = props;
  const { history, cms } = useContext(PrivateContext) || {};
  const { isOnboarding, onNext, onPrevious } = useContext(OnboardingContext);

  const [values, setValues] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [bulkProductUploadRequest, { loading: bulkUploadLoading }] = useMutation(BULK_UPLOAD);
  const [sheetActive, setSheetActive] = useState(false);
  const [sheetTitle, setSheetTitle] = useState("");
  const [sheetContent, setSheetContent] = useState("");

  const getCSVData = (csvValue) => {
    const regexForEnterKey = /\n,|\n/gi;
    const csvDataAfterRegex = csvValue.data.replace(regexForEnterKey, "\n");
    return csvDataAfterRegex;
  };

  const onSubmit = async () => {
    if (!values.data) {
      setBanner({
        isOpen: true,
        title: cms("common.message.error.csvIsRequired"),
        status: constant.CRITICAL,
      });
      return;
    }
    const modifiedFormat = getCSVData(values);
    const requestData = {
      file: {
        file: modifiedFormat,
        fileName: values.fileName,
      },
    };
    try {
      const res = await bulkProductUploadRequest({
        variables: {
          input: requestData,
        },
      });

      const resData = baseHelper.getResponseData(res.data, GQL_BULK_PRODUCT_CSV);
      let banner = {
        isOpen: true,
        status: "info",
        title: cms("section.bulk.message.success.title"),
        children: cms("section.bulk.message.success.content"),
        action: {
          id: "viewLogs",
          content: cms("section.bulk.message.success.action"),
          onAction: () => history.push("/activity/bulk"),
        },
      };

      if (!resData) {
        const error = baseHelper.getResponseError(res.data, GQL_BULK_PRODUCT_CSV);
        banner = {
          action: null,
          isOpen: true,
          status: constant.CRITICAL,
          title: error,
        };
      }
      setIsSubmitDisabled(true);
      setBanner(banner);

      if (isOnboarding && resData) {
        onNext();
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };
  const primaryAction = {
    content: cms("common.label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("common.button.cancel"),
    onAction: () => setSheetActive(false),
  };
  const learnMore = (title, content) => {
    setSheetActive(true);
    setSheetTitle(title);
    setSheetContent(content);
  };

  const downloadCSV = () => {
    const csv = Baby.unparse(addProductConfig);
    fileDownload(csv, BULK_PRODUCT_CSV);
  };

  useEffect(() => {
    setIsSubmitDisabled(!values.data);
  }, [values]);

  return (
    <>
      <Card
        title={cms("section.bulk.title")}
        actions={[
          {
            content: cms("common.label.learnMore"),
            onAction: () => learnMore(cms("section.bulk.title"), "TODO:"),
          },
        ]}
      >
        <Card.Section>
          <TextContainer>
            {cms("section.bulk.description")}
            <br />
            <Banner status="info">
              <p>{cms("section.bulk.label.sampleCsv")}</p>
            </Banner>
          </TextContainer>
          <br />
          <FormLayout>
            <FileUpload setValue={setValues} getCSV={downloadCSV} values={values} />
          </FormLayout>
          <PageActions
            primaryAction={{
              content: cms("section.bulk.button.primary"),
              id: "submitButton",
              onAction: () => onSubmit(),
              loading: bulkUploadLoading,
              disabled: bulkUploadLoading || !Object.values(values).length || isSubmitDisabled,
            }}
            secondaryActions={[
              {
                content: cms("common.button.cancel"),
                id: "cancelButton",
                onAction: () => (isOnboarding && onPrevious()) || history.push("/"),
              },
            ]}
          />
        </Card.Section>
      </Card>
      <Sheet
        title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {sheetContent}
      </Sheet>
    </>
  );
};

BulkCreate.propTypes = {
  setBanner: PropTypes.func.isRequired,
};

export default withFeature(BulkCreate, { feature: PRODUCT_BULK_CREATE });
